/**
 * Snaps.js
 * 
 * This module handles the prioritization and management of different types of snapping
 * in the Eagle Eye Designer application. It provides functionality to determine which
 * snap point (vertex snap or 90-degree snap) should be used based on proximity,
 * and manages the visual feedback for active snaps.
 */

import {DrawingGlobals, LABELS} from "../../Global/variables";
import {COLORS} from "../../Global/materials";

/**
 * Determines the final snap point based on available snap options and manages visual feedback
 * 
 * This function decides which snap point to use when multiple snap options are available,
 * and updates the visual state of the application to reflect the active snap type.
 * Visual feedback includes:
 * - Showing/hiding vertex indicators
 * - Changing line colors when snapping to 90 degrees
 * - Showing/hiding angle labels
 * 
 * @param {THREE.Vector3} snapPoint - Current snap point from previous frame (if any)
 * @param {Object|null} snapVertex - Vertex object to snap to (if any)
 * @param {THREE.Vector3|null} snap90 - 90-degree snap point (if any)
 * @param {THREE.Vector3|null} lengthSnap - length snap point (if any)
 * @param {THREE.Vector3} point - Current cursor position
 * @returns {THREE.Vector3} The final snap point to use
 */
export function getSnapPoint(snapPoint, snapVertex, snap90, lengthSnap, point) {
    // Determine closest among available snaps
    snapPoint = findClosestSnapPoint(point, snapVertex, snap90, lengthSnap);

    const lineMeshes = DrawingGlobals.lineMeshes || [];
    const hasLines = lineMeshes.length > 0;

    // Reset all drawing edges to base red each frame
    if (hasLines) {
        for (const line of lineMeshes) {
            line.material.color.setHex(COLORS.red);
        }
    }

    // Default to raw cursor if no snaps
    if (!snapPoint) {
        // reset angle label and vertex opacity
        LABELS.angle.visible = false;
        if (snapVertex) {
            snapVertex.material.opacity = 0;
        }
        return point;
    }

    // Visuals for vertex snap
    if (snapVertex && snapPoint === snapVertex.position) {
        snapVertex.material.opacity = 1;
    }

    const is90Snap = !!(snap90 && snapPoint.equals(snap90));
    const isLengthSnap = !!(lengthSnap && snapPoint.equals(lengthSnap));

    // Visuals for 90° snap: all edges green
    if (is90Snap && hasLines) {
        for (const line of lineMeshes) {
            line.material.color.setHex(COLORS.green);
        }
    }

    // Visuals for length snap: matched edges + current drawing edge blue
    if (isLengthSnap && hasLines) {
        const matched = DrawingGlobals.lengthSnapMatchedEdges || [];
        if (matched.length > 0) {
            // previously drawn edges that match the length
            for (const idx of matched) {
                const line = lineMeshes[idx];
                if (line) {
                    line.material.color.setHex(0x0033aa);
                }
            }
        }

        // also color the current edge being drawn (the last line) blue
        const currentLine = lineMeshes[lineMeshes.length - 1];
        if (currentLine) {
            currentLine.material.color.setHex(0x0033aa);
        }

        console.log("Length snap active on edges:", matched, "and current edge");
    }

    // If 90° is not the chosen snap for this frame, hide the angle label,
    // unless something else (like external logic) keeps it visible.
    if (!is90Snap) {
        LABELS.angle.visible = false;
    }

    // Hide vertex indicator if not used
    if (snapVertex && (snapVertex.position !== snapPoint)) {
        snapVertex.material.opacity = 0;
    }

    return snapPoint;
}

/**
 * Finds the closest snap point to the current cursor position
 * 
 * This function determines which snap point (vertex snap, 90-degree snap,
 * or length snap) should be used based on which one is closer to the
 * current cursor position and simple priority rules.
 * 
 * @param {THREE.Vector3} point - Current cursor position
 * @param {Object|null} snapVertex - Vertex object to potentially snap to
 * @param {THREE.Vector3|null} snap90 - 90-degree snap point to potentially snap to
 * @param {THREE.Vector3|null} lengthSnap - length snap point to potentially snap to
 * @returns {THREE.Vector3|null} The closest snap point, or null if no valid snap points
 */
function findClosestSnapPoint(point, snapVertex, snap90, lengthSnap) {
    const vertexP = (snapVertex && snapVertex.position) ? snapVertex.position : null;
    const lenP = lengthSnap || null;
    const rightP = snap90 || null;

    // If nothing available
    if (!vertexP && !lenP && !rightP) return null;

    // Prefer vertex if it's clearly closest against length snap
    if (vertexP && lenP) {
        const dv = vertexP.distanceTo(point);
        const dl = lenP.distanceTo(point);
        if (dv + 1e-6 < dl) {
            return vertexP;
        }
        // Prefer length snap when present over 90° to encourage equal-length snapping
        return lenP;
    }

    // If length snap exists (and no vertex), prefer it over 90°
    if (lenP) return lenP;

    // Otherwise pick between vertex and 90° by closeness
    if (vertexP && rightP) {
        return (vertexP.distanceTo(point) <= rightP.distanceTo(point)) ? vertexP : rightP;
    }

    return vertexP || rightP;
}
